import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFirebase } from "../../../context/FirebaseContext";
import { collection, query, orderBy, limit, startAfter, getDocs, where } from "firebase/firestore";
import { db, DEFAULT_STORE_ID } from "../../../firebase/firebase";
import "./AdminCustomers.css";

const AdminCustomers = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [customerDetailId, setCustomerDetailId] = useState(null);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    const navigate = useNavigate();
    const { } = useFirebase(); // Keep the context available for future use

    // Load initial customers on component mount
    useEffect(() => {
        if (DEFAULT_STORE_ID) {
            fetchCustomers();
        }
    }, [DEFAULT_STORE_ID]);

    // Fetch customers from Firestore
    const fetchCustomers = async (isLoadMore = false) => {
        if (!DEFAULT_STORE_ID) {
            setError("Store ID not available. Please try refreshing the page.");
            setLoading(false);
            return;
        }

        if (isLoadMore && !hasMore) return;

        try {
            setLoading(true);
            // console.log("Fetching customers for store:", DEFAULT_STORE_ID);

            // First, fetch customers from the store's customers collection
            const customersRef = collection(db, "stores", DEFAULT_STORE_ID, "customers");

            const queryLimit = 20;
            let queryConstraints = [
                orderBy("createdAt", "desc"),
                limit(queryLimit)
            ];

            if (isLoadMore && lastDoc) {
                queryConstraints.push(startAfter(lastDoc));
            }

            const customersQuery = query(customersRef, ...queryConstraints);
            const querySnapshot = await getDocs(customersQuery);

            const customersData = [];
            let lastVisible = null;

            // Process each customer document
            for (const doc of querySnapshot.docs) {
                const customerData = doc.data();
                const customerId = doc.id;

                // Get order data from the store's orders collection
                // instead of the user's orders collection
                let orderCount = 0;
                let totalSpent = 0;
                let lastOrderDate = null;
                let recentOrders = [];

                try {
                    // Fetch orders from store's orders collection for this customer
                    const storeOrdersRef = collection(db, "stores", DEFAULT_STORE_ID, "orders");
                    const storeOrdersQuery = query(
                        storeOrdersRef,
                        where("userId", "==", customerId),
                        orderBy("createdAt", "desc")
                    );
                    const storeOrdersSnapshot = await getDocs(storeOrdersQuery);

                    // Calculate order metrics
                    orderCount = storeOrdersSnapshot.size;

                    if (orderCount > 0) {
                        // Get the most recent order date
                        const firstOrder = storeOrdersSnapshot.docs[0].data();
                        lastOrderDate = firstOrder.createdAt;

                        // Calculate total spent and collect recent orders
                        storeOrdersSnapshot.docs.forEach((orderDoc, index) => {
                            const orderData = orderDoc.data();

                            // Add to total spent if there's a valid total
                            if (orderData.total && !isNaN(parseFloat(orderData.total))) {
                                totalSpent += parseFloat(orderData.total);
                            }

                            // Collect up to 5 recent orders
                            if (index < 5) {
                                recentOrders.push({
                                    id: orderDoc.id,
                                    orderNumber: orderData.orderNumber || orderDoc.id.substring(0, 8),
                                    total: orderData.total || 0,
                                    status: orderData.status || 'Processing',
                                    createdAt: orderData.createdAt
                                });
                            }
                        });
                    }
                } catch (orderError) {
                    // console.error(`Error fetching orders for customer ${customerId}:`, orderError);
                    // Continue with default values if there's an error
                }

                // Create the customer object with all data
                customersData.push({
                    id: customerId,
                    name: customerData.customerInfo?.firstName + " " + customerData.customerInfo?.lastName || "Guest Customer",
                    email: customerData.email || "",
                    phone: customerData.customerInfo?.phone || "",
                    address: customerData.address || null,
                    orderCount: orderCount,
                    totalSpent: totalSpent,
                    averageOrderValue: orderCount > 0 ? totalSpent / orderCount : 0,
                    lastOrderDate: lastOrderDate,
                    createdAt: customerData.createdAt || null,
                    recentOrders: recentOrders,
                    notes: customerData.notes || ""
                });

                lastVisible = doc;
            }

            // console.log("Customers loaded:", customersData.length);

            if (isLoadMore) {
                setCustomers([...customers, ...customersData]);
            } else {
                setCustomers(customersData);
            }

            setLastDoc(lastVisible);
            setHasMore(customersData.length === queryLimit);
            setError("");
        } catch (err) {
            console.error("Error fetching customers:", err);
            setError("Failed to load customers. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Handle load more
    const handleLoadMore = () => {
        fetchCustomers(true);
    };

    // Filter customers based on search
    const filteredCustomers = customers.filter(customer => {
        const searchLower = search.toLowerCase();
        return (
            customer.name?.toLowerCase().includes(searchLower) ||
            customer.email?.toLowerCase().includes(searchLower) ||
            customer.phone?.toLowerCase().includes(searchLower)
        );
    });

    // Handle bulk selection
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedCustomers(filteredCustomers.map(customer => customer.id));
        } else {
            setSelectedCustomers([]);
        }
    };

    // Handle individual selection
    const handleSelectCustomer = (customerId) => {
        if (selectedCustomers.includes(customerId)) {
            setSelectedCustomers(selectedCustomers.filter(id => id !== customerId));
        } else {
            setSelectedCustomers([...selectedCustomers, customerId]);
        }
    };

    // View customer details
    const handleViewDetails = (customerId) => {
        setCustomerDetailId(customerId);
    };

    // Export customer data (CSV format)
    const handleExportCustomers = () => {
        if (customers.length === 0) {
            alert("No customers to export");
            return;
        }

        // Get selected customers or all if none selected
        const dataToExport = selectedCustomers.length > 0
            ? customers.filter(c => selectedCustomers.includes(c.id))
            : customers;

        // Create CSV headers
        const headers = [
            "Name",
            "Email",
            "Phone",
            "Address",
            "Total Orders",
            "Total Spent",
            "Created At"
        ];

        // Create CSV rows
        const rows = dataToExport.map(customer => [
            customer.name || "",
            customer.email || "",
            customer.phone || "",
            formatAddress(customer.address) || "",
            customer.orderCount || "0",
            formatCurrency(customer.totalSpent || 0),
            formatDate(customer.createdAt)
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(","),
            ...rows.map(row => row.join(","))
        ].join("\n");

        // Create download link
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.setAttribute("href", url);
        link.setAttribute("download", `customers_${DEFAULT_STORE_ID}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Format address for display
    const formatAddress = (address) => {
        if (!address) return "";

        const parts = [];
        if (address.street) parts.push(address.street);
        if (address.city) parts.push(address.city);
        if (address.state) parts.push(address.state);
        if (address.postalCode) parts.push(address.postalCode);
        if (address.country) parts.push(address.country);

        return parts.join(", ");
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `AED ${parseFloat(amount).toFixed(2)}`;
    };

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp) return "";

        let date;
        if (timestamp.toDate) {
            // Firestore timestamp
            date = timestamp.toDate();
        } else if (timestamp.seconds) {
            // Firestore timestamp in seconds
            date = new Date(timestamp.seconds * 1000);
        } else {
            // ISO string or other format
            date = new Date(timestamp);
        }

        return date.toISOString().split('T')[0];
    };

    // Find the customer with the specified ID
    const selectedCustomer = customerDetailId
        ? customers.find(c => c.id === customerDetailId)
        : null;

    // Close customer details panel
    const closeCustomerDetails = () => {
        setCustomerDetailId(null);
    };

    // Initial loading state
    if (loading && customers.length === 0) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <p>Loading customers...</p>
            </div>
        );
    }

    // No store ID available
    if (!DEFAULT_STORE_ID) {
        return (
            <div className="admin-error-container">
                <h2>Store ID Not Available</h2>
                <p>Please try refreshing the page or contact support if the issue persists.</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
            </div>
        );
    }

    return (
        <div className="admin-customers">
            {/* Page Title */}
            <div className="admin-page-title">
                <h1>Customers</h1>
                <p>Manage your store customers</p>
            </div>

            {error && <div className="admin-error-message">{error}</div>}

            {/* Actions Bar */}
            <div className="customers-actions-bar">
                <div className="customers-actions-left">
                    <button
                        className="admin-secondary-btn"
                        onClick={handleExportCustomers}
                        disabled={loading}
                    >
                        {selectedCustomers.length > 0
                            ? `Export ${selectedCustomers.length} Customers`
                            : "Export All Customers"}
                    </button>

                    {selectedCustomers.length > 0 && (
                        <span className="selected-count">
                            {selectedCustomers.length} selected
                        </span>
                    )}
                </div>

                <div className="customers-actions-right">
                    <div className="customer-search">
                        <input
                            type="text"
                            placeholder="Search customers..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Customers Table */}
            <div className="admin-table-container customers-table">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={
                                        selectedCustomers.length === filteredCustomers.length &&
                                        filteredCustomers.length > 0
                                    }
                                />
                            </th>
                            <th>Customer</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Orders</th>
                            <th>Total Spent</th>
                            <th>Last Order</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCustomers.length > 0 ? (
                            filteredCustomers.map(customer => (
                                <tr key={customer.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedCustomers.includes(customer.id)}
                                            onChange={() => handleSelectCustomer(customer.id)}
                                        />
                                    </td>
                                    <td className="customer-name-cell">
                                        <div className="customer-avatar">
                                            {customer.name?.charAt(0).toUpperCase() || "?"}
                                        </div>
                                        <span>{customer.name || "Guest Customer"}</span>
                                    </td>
                                    <td>{customer.email || "—"}</td>
                                    <td>{customer.phone || "—"}</td>
                                    <td>{customer.orderCount || "0"}</td>
                                    <td>{formatCurrency(customer.totalSpent || 0)}</td>
                                    <td>{customer.lastOrderDate ? formatDate(customer.lastOrderDate) : "—"}</td>
                                    <td>
                                        <button
                                            className="view-customer-btn"
                                            onClick={() => handleViewDetails(customer.id)}
                                        >
                                            View Details
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="no-results">
                                    {search
                                        ? "No customers found matching your search."
                                        : "No customers found. Customer data will appear when you receive orders."}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Load More */}
            {hasMore && customers.length > 0 && (
                <div className="load-more-container">
                    <button
                        className="load-more-btn"
                        onClick={handleLoadMore}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Load More Customers"}
                    </button>
                </div>
            )}

            {/* Customer Details Panel */}
            {selectedCustomer && (
                <div className="customer-details-panel">
                    <div className="panel-header">
                        <h2>Customer Details</h2>
                        <button
                            className="close-panel-btn"
                            onClick={closeCustomerDetails}
                        >
                            ×
                        </button>
                    </div>

                    <div className="panel-content">
                        <div className="customer-profile">
                            <div className="customer-avatar large">
                                {selectedCustomer.name?.charAt(0).toUpperCase() || "?"}
                            </div>
                            <div className="customer-info">
                                <h3>{selectedCustomer.name || "Guest Customer"}</h3>
                                <p className="customer-since">
                                    Customer since {formatDate(selectedCustomer.createdAt)}
                                </p>
                            </div>
                        </div>

                        <div className="customer-stats">
                            <div className="stat-box">
                                <span className="stat-value">
                                    {selectedCustomer.orderCount || "0"}
                                </span>
                                <span className="stat-label">Orders</span>
                            </div>
                            <div className="stat-box">
                                <span className="stat-value">
                                    {formatCurrency(selectedCustomer.totalSpent || 0)}
                                </span>
                                <span className="stat-label">Total Spent</span>
                            </div>
                            <div className="stat-box">
                                <span className="stat-value">
                                    {selectedCustomer.averageOrderValue
                                        ? formatCurrency(selectedCustomer.averageOrderValue)
                                        : "AED 0.00"}
                                </span>
                                <span className="stat-label">Avg Order</span>
                            </div>
                        </div>

                        <div className="customer-details-section">
                            <h4>Contact Information</h4>
                            <div className="details-grid">
                                <div className="detail-item">
                                    <span className="detail-label">Email</span>
                                    <span className="detail-value">{selectedCustomer.email || "—"}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Phone</span>
                                    <span className="detail-value">{selectedCustomer.phone || "—"}</span>
                                </div>
                            </div>
                        </div>


                        <div className="customer-details-section">
                            <h4>Recent Orders</h4>
                            {selectedCustomer.recentOrders && selectedCustomer.recentOrders.length > 0 ? (
                                <div className="recent-orders">
                                    {selectedCustomer.recentOrders.map(order => (
                                        <div key={order.id} className="order-item">
                                            <div className="order-header">
                                                <span className="order-id">#{order.orderNumber}</span>
                                                <span className="order-date">{formatDate(order.createdAt)}</span>
                                            </div>
                                            <div className="order-details">
                                                <span className="order-amount">{formatCurrency(order.total)}</span>
                                                <span className={`order-status status-${order.status.toLowerCase()}`}>
                                                    {order.status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        className="view-all-orders-btn"
                                        onClick={() => navigate(`/admin/orders?customer=${selectedCustomer.id}`)}
                                    >
                                        View All Orders
                                    </button>
                                </div>
                            ) : (
                                <p className="no-orders">No orders found for this customer.</p>
                            )}
                        </div>


                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCustomers;